import { Badge, Box, Card, CardContent, Chip, FormGroup, IconButton, InputAdornment } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import DropdownMenu from "../DropdownMenu";
import {
    FaCheckCircle,
    FaChevronDown,
    FaChevronUp,
    FaReply,
    FaToggleOff,
    FaToggleOn,
    FaTrashAlt,
} from "react-icons/fa";
import { FormDataContext } from "../contexts/FormContext";
import { useSelector } from "react-redux";
import { displayNotification } from "helpers";
import {
    useCreateFormElementCommentMutation,
    useDeleteFormElementCommentMutation,
    useSetFormElementCommentResolvedStatusMutation,
} from "modules/pvs_activities/redux/service/formComments";
import TextAreaField from "../core/TextAreaField";
import { initialCommentFormFields } from "modules/pvs_activities/constants";
import moment from "moment";
import { uniqueId } from "lodash";

const CommentElement = ({ elementId, data, onModifyComments, isReply }) => {
    const formDataContext = useContext(FormDataContext);
    const authData = useSelector((s) => s.auth.data);

    const [deleteFormElementComment, deleteCommentRes] = useDeleteFormElementCommentMutation();
    const [setFormElementCommentResolvedStatus, setFormElementCommentResolvedStatusRes] =
        useSetFormElementCommentResolvedStatusMutation();
    const [createFormElementComment, createCommentRes] = useCreateFormElementCommentMutation();
    const [showReplies, setShowReplies] = useState(false);
    const [form, setForm] = useState({ ...initialCommentFormFields });

    useEffect(() => {
        if (deleteCommentRes?.isError) {
            displayNotification("error", "Error deleting Comment");
            console.error({ error: deleteCommentRes.error });
        }
        if (deleteCommentRes.isSuccess) {
            displayNotification("success", "Successfully deleted Comment");
        }
    }, [deleteCommentRes]);

    useEffect(() => {
        if (setFormElementCommentResolvedStatusRes?.isError) {
            displayNotification("error", "Error setting comment status");
            console.error({ error: setFormElementCommentResolvedStatusRes.error });
        }
        if (setFormElementCommentResolvedStatusRes.isSuccess) {
            displayNotification("success", "Successfully set comment status");
        }
    }, [setFormElementCommentResolvedStatusRes]);

    useEffect(() => {
        if (createCommentRes?.isError) {
            displayNotification("error", "Error creating comment reply");
            console.error({ error: createCommentRes.error });
        }
        if (createCommentRes.isSuccess) {
            setForm({ ...initialCommentFormFields });
            displayNotification("success", "Successfully created comment reply");
        }
    }, [createCommentRes]);

    const handleResolveStatus = (id, isResolved) => {
        const statusInverseText = isResolved ? "Unresolved" : "Resolved";
        if (window.confirm(`You are about to mark comment as ${statusInverseText}. Are you sure?`)) {
            if (formDataContext.mode === "preview") {
                onModifyComments((comments) => {
                    const tmp = [...comments];
                    const check = tmp.findIndex((r) => r.id === id);
                    if (check !== -1) {
                        tmp[check].is_resolved = !tmp[check].is_resolved;
                        displayNotification("success", "Successfully set comment status");
                    }
                    return tmp;
                });
            } else {
                setFormElementCommentResolvedStatus({
                    id,
                    status: !isResolved,
                });
            }
        }
    };

    const handleDelete = (id) => {
        if (window.confirm("You are about to delete a comment. Are you sure?")) {
            if (formDataContext.mode === "preview") {
                onModifyComments((comments) => {
                    displayNotification("success", "Successfully deleted comment");
                    return comments.filter((r) => r.id !== id);
                });
            } else {
                deleteFormElementComment(id);
            }
        }
    };

    const handleReplyAdd = (e) => {
        e.preventDefault();
        if (formDataContext.mode === "preview") {
            onModifyComments((comments) => {
                const tmp = [...comments];
                const check = tmp.findIndex((r) => r.id === data.id);
                if (check !== -1) {
                    tmp[check].replies = [
                        {
                            ...form,
                            user_id: authData.id,
                            user_name: authData.name,
                            role_name: authData?.role?.role_name ?? '-',
                            element_id: elementId,
                            is_resolved: false,
                            created_date: moment().format("DD-MMMM-YYYY (hh:mm A)"),
                            id: uniqueId(),
                        },
                        ...tmp[check].replies,
                    ];
                    setForm({ ...initialCommentFormFields });
                    displayNotification("success", "Successfully saved comment reply");
                }
                return tmp;
            });
        } else {
            createFormElementComment({
                ...form,
                user_id: authData.id,
                element_id: elementId,
                template_id: formDataContext.templateId,
                section_id: formDataContext.sectionId,
                movement_id: formDataContext.movementId,
                parent_id: data.id,
            });
        }
    };

    // Only For Preview
    const handleModifyReplies = (callback) => {
        onModifyComments((comments) => {
            const tmp = [...comments];
            const check = tmp.findIndex((r) => r.id === data.id);
            if (check !== -1) {
                const ret = callback(data.replies);
                if (ret) {
                    comments[check].replies = [...ret];
                }
            }
            return tmp;
        });
    };

    const ReplyIcon = showReplies ? FaChevronUp : FaChevronDown;

    return (
        <Card sx={{ mb: 1, border: "1px solid #ccc" }} className={`bg-light-3`} elevation={0}>
            <CardContent sx={{ px: 0, pb: "0px !important", pt: 1 }}>
                <Box sx={{ display: "flex", borderBottom: "1px solid #ccc", px: 2, pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <div>
                            {isReply === false && (
                                <strong style={{ display: "flex", alignItems: "center" }}>
                                    {!["FT_LTR"].includes(formDataContext.formType) && (
                                        <>
                                            <small style={{ fontSize: 8 }}>
                                                <span>{String(data.type).toUpperCase()}</span>
                                                <span className="mx-2">-</span>
                                            </small>
                                        </>
                                    )}
                                    <small
                                        style={{ fontSize: 8 }}
                                        className={data.is_resolved ? "text-success" : "text-danger"}
                                    >
                                        {String(data.is_resolved ? "Resolved" : "Unresolved").toUpperCase()}
                                    </small>
                                </strong>
                            )}
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <strong>{data.user_name}</strong>
                                <span>-</span>
                                <span>({data.role_name})</span>
                            </div>
                        </div>
                    </Box>
                    <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                        <small>{data.created_date}</small>
                        {isReply === false && (
                            <Badge
                                color="themeTwo"
                                badgeContent={data.replies.length}
                                sx={{ mx: 1, "& .MuiBadge-badge": { border: "1px solid #faccdd" } }}
                            >
                                <Chip
                                    icon={<ReplyIcon style={{ fontSize: 10, marginLeft: 5 }} />}
                                    label={`Replies`}
                                    color="black"
                                    size="small"
                                    onClick={() => setShowReplies(!showReplies)}
                                />
                            </Badge>
                        )}
                        <DropdownMenu
                            sx={{
                                zIndex: 999999999 + 1,
                            }}
                            buttonSx={{
                                ml: 1,
                                maxHeight: "24px",
                                maxWidth: "24px",
                                minWidth: "24px",
                            }}
                            size="small"
                            disabled={
                                !(formDataContext.isReadOnly === false && String(authData.id) === String(data.user_id))
                            }
                            items={
                                formDataContext.isReadOnly === false && String(authData.id) === String(data.user_id)
                                    ? [
                                          ...(isReply === false
                                              ? [
                                                    {
                                                        icon: !data.is_resolved ? <FaToggleOn /> : <FaToggleOff />,
                                                        text: `Mark as ${data.is_resolved ? "Unresolved" : "Resolved"}`,
                                                        onClick: () => handleResolveStatus(data.id, data.is_resolved),
                                                    },
                                                ]
                                              : []),
                                          {
                                              icon: <FaTrashAlt />,
                                              text: "Delete",
                                              onClick: () => handleDelete(data.id),
                                          },
                                      ]
                                    : []
                            }
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        "& p": {
                            mb: 0,
                        },
                        "& *": {
                            wordBreak: "break-all",
                        },
                    }}
                >
                    <div dangerouslySetInnerHTML={{ __html: data.content }} />
                </Box>
                {isReply === false && showReplies && (
                    <Box
                        sx={{
                            borderTop: "1px solid #ccc",
                            // pl: 4,
                            px: 2,
                            py: 1,
                            "& > div:last-child > div": {
                                mb: 0,
                            },
                        }}
                        className="bg-light-2"
                    >
                        {((data.replies.length > 0 && String(data.replies[0].user_id) !== String(authData.id)) ||
                            (data.replies.length === 0 && String(data.user_id) !== String(authData.id))) && (
                            <FormGroup row sx={{ mb: "5px", width: "100%" }}>
                                <TextAreaField
                                    value={form.content}
                                    onChange={(value) => setForm({ ...form, content: value })}
                                    label={"Add a reply"}
                                    placeholder={"Write your reply text here..."}
                                    minRows={3}
                                    maxRows={3}
                                    size={"small"}
                                    multiline
                                    fullWidth={false}
                                    marginTop={0}
                                    sx={{
                                        backgroundColor: "#fff",
                                        flexGrow: 1,
                                    }}
                                    onKeyDown={(e) => e.keyCode === 13 && handleReplyAdd(e)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    variant="contained"
                                                    color="success"
                                                    disableElevation
                                                    onClick={handleReplyAdd}
                                                    sx={{ height: 45, width: 45, fontSize: "1.75rem" }}
                                                >
                                                    <FaCheckCircle />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormGroup>
                        )}
                        {data.replies.length > 0 ? (
                            data.replies.map((r) => (
                                <Box key={r.id} sx={{ position: "relative", pl: 3 }}>
                                    <FaReply
                                        style={{
                                            transform: "scale(-1)",
                                            position: "absolute",
                                            left: "-5px",
                                            top: "10px",
                                            color: "var(--primary)",
                                            fontSize: "1.5rem",
                                        }}
                                    />
                                    <CommentElement data={r} onModifyComments={handleModifyReplies} isReply={true} />
                                </Box>
                            ))
                        ) : (
                            <Card sx={{ border: "1px solid #ccc" }} className={`bg-light-3`} elevation={0}>
                                <CardContent sx={{ px: 0, py: "5px !important", textAlign: "center" }}>
                                    No Replies Available
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

CommentElement.defaultProps = {
    elementId: "",
    data: {},
    onModifyComments: () => {},
    onReplyAdd: () => {},
    onDelete: () => {},
    onResolveStatus: () => {},
    isReply: false,
};

export default CommentElement;
